import React, { useEffect, useMemo, useState, useRef } from "react";
import {
    Box,
    Typography,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Divider,
    Badge,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SendIcon from "@material-ui/icons/Send";
import CloseIcon from "@material-ui/icons/Close";
import NoteService from "./Locality/Service/noteService";
import StudentService from "./Locality/Service/studentService";
import SocketService from "./Locality/Service/socketService";
import PageTitle from "../../components/PageTitle/PageTitle";
import Widget from "../../components/Widget/Widget";

const useStyles = makeStyles((theme) => ({
    yellowHeader: {
        backgroundColor: '#FFC107',
        color: 'white',
        padding: '16px 24px',
        marginBottom: '24px',
        borderRadius: '4px 4px 0 0',
    },
    layout: {
        display: 'flex',
        gap: 16,
        minHeight: 600,
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    studentsPanel: {
        width: 300,
        flexShrink: 0,
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    notesPanel: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    notesContainer: {
        flex: 1,
        overflowY: 'auto',
        padding: 16,
        backgroundColor: '#f5f5f5',
    },
    noteBubble: {
        padding: '12px 16px',
        borderRadius: 18,
        marginBottom: 16,
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        color: '#856404',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#ffe082',
            transform: 'scale(1.01)',
            transition: 'all 0.2s ease',
        },
    },
    noteHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
        flexWrap: 'wrap',
    },
    noteType: {
        fontWeight: 700,
        fontSize: '0.8rem',
        marginBottom: 6,
    },
    noteText: {
        fontSize: '0.9rem',
        marginBottom: 6,
    },
    noteTime: {
        fontSize: '0.7rem',
        opacity: 0.75,
    },
    chatDialog: {
        '& .MuiDialog-paper': {
            width: '500px',
            maxWidth: '90vw',
            height: '600px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
        },
    },
    chatHeader: {
        backgroundColor: '#2196F3',
        color: 'white',
        padding: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    chatMessages: {
        flex: 1,
        overflowY: 'auto',
        padding: 16,
        backgroundColor: '#f5f5f5',
    },
    chatMessageBubble: {
        padding: '10px 16px',
        borderRadius: 18,
        marginBottom: 12,
        maxWidth: '70%',
        wordWrap: 'break-word',
    },
    messageSent: {
        backgroundColor: '#2196F3',
        color: 'white',
        marginLeft: 'auto',
        textAlign: 'right',
    },
    messageReceived: {
        backgroundColor: '#e0e0e0',
        color: '#000',
        marginRight: 'auto',
    },
    chatInput: {
        padding: 16,
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        gap: 8,
        alignItems: 'center',
    },
}));

export default function NoteList() {
    const classes = useStyles();
    const userDetails = useMemo(() => JSON.parse(localStorage.getItem("userDetail") || "{}"), []);

    const [studentsList, setStudentsList] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [notesList, setNotesList] = useState([]);
    const [chatOpen, setChatOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [unreadCounts, setUnreadCounts] = useState({}); // { noteId: count }
    const [studentUnreadCounts, setStudentUnreadCounts] = useState({}); // { studentId: totalCount }
    const lastReadTimesRef = useRef({}); // { noteId: timestamp }
    const pollingIntervalRef = useRef(null);
    const socketInitializedRef = useRef(false);
    const chatMessagesEndRef = useRef(null);
    const groupedDataMapRef = useRef(new Map()); // Store grouped notes data

    useEffect(() => {
        loadStudents();
        
        // Load last read times from localStorage
        const savedLastReadTimes = localStorage.getItem('noteListLastReadTimes');
        if (savedLastReadTimes) {
            try {
                lastReadTimesRef.current = JSON.parse(savedLastReadTimes);
            } catch (e) {
                console.error('Error parsing last read times:', e);
            }
        }
        
        // Initialize socket connection
        if (!socketInitializedRef.current) {
            const socket = SocketService.initializeSocket();
            if (socket) {
                // Listen for messages on all notes (for unread count updates)
                // This listener will catch messages from any note the user has access to
                socket.on('receive-message', (messageData) => {
                    // Only handle if chat is not currently open for this note
                    // (if chat is open, the room-specific handler will take care of it)
                    if (!chatOpen || selectedNote?._id !== messageData.noteId) {
                        handleIncomingMessage(messageData);
                    }
                });
            }
            socketInitializedRef.current = true;
        }
        
        // Reduced polling - only as fallback if socket is disconnected (every 60 seconds)
        pollingIntervalRef.current = setInterval(async () => {
            // Only poll if socket is not connected
            if (!SocketService.isConnected()) {
                try {
                    const schooleId = userDetails?.schooleId;
                    if (schooleId) {
                        // For TEACHER role, filter by classId
                        const classId = userDetails?.role === 'TEACHER' ? userDetails?.classId : null;
                        const allNotesRes = await NoteService.getAllNotes(classId);
                        const allNotes = allNotesRes || [];
                        await checkForUnreadMessages(allNotes);
                    }
                } catch (err) {
                    console.error('Error polling for unread messages:', err);
                }
            }
        }, 60000); // Increased to 60 seconds as fallback only
        
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
            // Disconnect socket on unmount
            SocketService.disconnectSocket();
            socketInitializedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedStudent) {
            loadNotesForStudent(selectedStudent._id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStudent]);

    const loadStudents = async () => {
        try {
            const schooleId = userDetails?.schooleId;
            if (!schooleId) return;
            
            // Use new aggregated API that groups notes by student
            const groupedData = await NoteService.getNotesGroupedByStudent();
            
            if (!groupedData || groupedData.length === 0) {
                setStudentsList([]);
                return;
            }
            
            // Transform grouped data to student list format
            const studentsWithMessages = groupedData.map(group => {
                const studentId = group.studentDetails.studentId?._id || group.studentDetails.studentId;
                // Get classId from first note if available
                const firstNote = group.studentNotes && group.studentNotes.length > 0 ? group.studentNotes[0] : null;
                return {
                    _id: studentId,
                    firstName: group.studentDetails.firstName,
                    lastName: group.studentDetails.lastName,
                    classId: firstNote?.classId || null,
                    // Store notes for this student (will be used when student is selected)
                    notes: group.studentNotes || []
                };
            });
            
            setStudentsList(studentsWithMessages);
            
            // Store grouped data for quick access when student is selected
            // Convert to a map for easy lookup
            const groupedDataMap = new Map();
            groupedData.forEach(group => {
                const studentId = group.studentDetails.studentId?._id || group.studentDetails.studentId;
                if (studentId) {
                    groupedDataMap.set(String(studentId), group.studentNotes || []);
                }
            });
            
            // Store in a ref for use in loadNotesForStudent
            groupedDataMapRef.current = groupedDataMap;
            
            // Flatten all notes for unread check
            const allNotes = groupedData.flatMap(group => group.studentNotes || []);
            
            // Only check unread messages on initial load if socket is not connected
            // Otherwise, WebSocket will handle real-time updates
            if (!SocketService.isConnected() && allNotes.length > 0) {
                checkForUnreadMessages(allNotes);
            }
            
            // Auto-select first student if available
            if (studentsWithMessages.length > 0 && !selectedStudent) {
                setSelectedStudent(studentsWithMessages[0]);
            }
        } catch (err) {
            console.error('Error fetching students:', err);
            setStudentsList([]);
        }
    };

    const loadNotesForStudent = async (studentId) => {
        try {
            if (!studentId) return;
            
            // Use cached grouped data if available (from loadStudents)
            if (groupedDataMapRef.current && groupedDataMapRef.current.has(String(studentId))) {
                const notes = groupedDataMapRef.current.get(String(studentId));
                setNotesList(notes);
                
                // Only check for unread messages if socket is not connected (WebSocket handles it otherwise)
                if (!SocketService.isConnected()) {
                    checkForUnreadMessages(notes);
                }
            } else {
                // Fallback: fetch from API if cached data not available
                const classId = userDetails?.role === 'TEACHER' ? userDetails?.classId : null;
                const res = await NoteService.getAllNotes(classId);
                const filtered = (res || []).filter(note => {
                    const noteStudentId = note?.studentId?._id || note?.studentId;
                    return String(noteStudentId) === String(studentId);
                });
                setNotesList(filtered);
                
                // Only check for unread messages if socket is not connected (WebSocket handles it otherwise)
                if (!SocketService.isConnected()) {
                    checkForUnreadMessages(filtered);
                }
            }
        } catch (err) {
            console.error('Error fetching notes:', err);
        }
    };

    // Handle incoming messages from WebSocket for unread count updates
    const handleIncomingMessage = (messageData) => {
        try {
            const currentUserId = userDetails?._id;
            const msgSenderId = messageData?.createdBy?._id || messageData?.createdBy;
            
            // Only update if message is from someone else
            if (currentUserId && msgSenderId && String(currentUserId) === String(msgSenderId)) {
                return; // Don't count own messages
            }
            
            const noteId = messageData.noteId || messageData.parentNoteId;
            if (!noteId) return;
            
            // Check if this note is in our current notes list
            const note = notesList.find(n => String(n._id) === String(noteId));
            if (!note) {
                // Note not in current list, might need to refresh students list
                // But don't make API call, just skip
                return;
            }
            
            // Get last read time for this note
            const lastReadTime = lastReadTimesRef.current[noteId] 
                ? new Date(lastReadTimesRef.current[noteId])
                : null;
            
            // Only count if message is newer than last read time
            if (lastReadTime && new Date(messageData.createdAt) <= lastReadTime) {
                return; // Already read
            }
            
            // Update unread count for this note
            setUnreadCounts(prev => {
                const updated = { ...prev };
                updated[noteId] = (updated[noteId] || 0) + 1;
                return updated;
            });
            
            // Update student unread count
            const studentId = note?.studentId?._id || note?.studentId;
            if (studentId) {
                const studentIdStr = String(studentId);
                setStudentUnreadCounts(prev => {
                    const updated = { ...prev };
                    updated[studentIdStr] = (updated[studentIdStr] || 0) + 1;
                    return updated;
                });
            }
        } catch (err) {
            console.error('Error handling incoming message:', err);
        }
    };

    const checkForUnreadMessages = async (notesToCheck = null) => {
        try {
            // Use provided notes or current state
            const currentNotes = notesToCheck || notesList;
            if (!currentNotes || currentNotes.length === 0) {
                setUnreadCounts({});
                setStudentUnreadCounts({});
                return;
            }
            
            const counts = {};
            const studentCounts = {};
            const currentUserId = userDetails?._id;
            
            for (const note of currentNotes) {
                if (!note?._id) continue;
                
                try {
                    const messages = await NoteService.getChatMessagesByNoteId(note._id);
                    const allMessages = Array.isArray(messages) ? messages : (messages?.data || []);
                    
                    // Get last read time for this note
                    const lastReadTime = lastReadTimesRef.current[note._id] 
                        ? new Date(lastReadTimesRef.current[note._id])
                        : null;
                    
                    // Count unread messages (chat messages only, not form notes)
                    const unreadCount = allMessages.filter(msg => {
                        // Only count chat messages (not form notes)
                        if (msg.messageKind !== 'CHAT') return false;
                        // Only count messages from others (not from current user)
                        const msgSenderId = msg?.createdBy?._id || msg?.createdBy;
                        if (currentUserId && msgSenderId && String(currentUserId) === String(msgSenderId)) {
                            return false;
                        }
                        // Count if message is newer than last read time
                        if (lastReadTime) {
                            return new Date(msg.createdAt) > lastReadTime;
                        }
                        // If never read, count all messages from others
                        return true;
                    }).length;
                    
                    if (unreadCount > 0) {
                        counts[note._id] = unreadCount;
                        
                        // Aggregate count by student
                        const studentId = note?.studentId?._id || note?.studentId;
                        if (studentId) {
                            const studentIdStr = String(studentId);
                            studentCounts[studentIdStr] = (studentCounts[studentIdStr] || 0) + unreadCount;
                        }
                    }
                } catch (err) {
                    console.error(`Error checking unread for note ${note._id}:`, err);
                }
            }
            setUnreadCounts(counts);
            setStudentUnreadCounts(studentCounts);
        } catch (err) {
            console.error('Error checking for unread messages:', err);
        }
    };

    const loadChatMessages = async (noteId) => {
        try {
            if (!noteId) return;
            const res = await NoteService.getChatMessagesByNoteId(noteId);
            setChatMessages(res || []);
        } catch (err) {
            console.error('Error fetching chat messages:', err);
        }
    };

    const handleSelectStudent = (student) => {
        setSelectedStudent(student);
        setNotesList([]);
    };

    const handleOpenChat = async (note) => {
        setSelectedNote(note);
        setChatOpen(true);
        if (note?._id) {
            // Load initial chat messages
            await loadChatMessages(note._id);
            
            // Join socket room for real-time updates
            SocketService.joinNoteChat(note._id, {
                onJoined: (data) => {
                    console.log('Joined room:', data);
                },
                onMessage: (messageData) => {
                    console.log('Received real-time message:', messageData);
                    // Add new message to chat
                    setChatMessages(prev => {
                        // Check if message already exists (avoid duplicates)
                        const exists = prev.some(msg => msg._id === messageData._id);
                        if (exists) return prev;
                        return [...prev, messageData];
                    });
                    
                    // Update unread count if message is from someone else and chat is not open
                    // (If chat is open, we're already viewing it, so no unread count needed)
                    const currentUserId = userDetails?._id;
                    const msgSenderId = messageData?.createdBy?._id || messageData?.createdBy;
                    if (currentUserId && msgSenderId && String(currentUserId) !== String(msgSenderId)) {
                        // Message is from someone else, but since chat is open, we're reading it
                        // So we don't need to increment unread count
                    }
                    
                    // Auto-scroll to bottom
                    setTimeout(() => {
                        if (chatMessagesEndRef.current) {
                            chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                        }
                    }, 100);
                },
                onError: (error) => {
                    console.error('Socket error:', error);
                },
            });
            
            // Mark as read - update last read time
            const now = new Date().toISOString();
            lastReadTimesRef.current[note._id] = now;
            localStorage.setItem('noteListLastReadTimes', JSON.stringify(lastReadTimesRef.current));
            
            // Clear unread count for this note
            setUnreadCounts(prev => {
                const updated = { ...prev };
                delete updated[note._id];
                return updated;
            });
            
            // Update student unread count (no API call needed - use current state)
            const noteStId = note?.studentId?._id || note?.studentId;
            if (noteStId) {
                const studentIdStr = String(noteStId);
                // Recalculate student count from current notesList state
                const studentNotes = notesList.filter(n => {
                    const noteStId2 = n?.studentId?._id || n?.studentId;
                    return String(noteStId2) === studentIdStr;
                });
                // Only check unread if we have notes, otherwise skip API call
                if (studentNotes.length > 0) {
                    await checkForUnreadMessages(studentNotes);
                }
            }
        }
    };

    const handleCloseChat = () => {
        // Leave socket room
        if (selectedNote?._id) {
            SocketService.leaveNoteChat(selectedNote._id);
        }
        setChatOpen(false);
        setSelectedNote(null);
        setChatMessages([]);
        setChatInput('');
    };

    const handleSendMessage = async () => {
        try {
            const text = (chatInput || '').trim();
            if (!text) return;
            if (!selectedNote?._id) return;

            const studentId = selectedNote?.studentId?._id || selectedNote?.studentId || selectedStudent?._id;
            if (!studentId) return;

            setChatInput('');
            
            // Update last read time immediately (optimistic update)
            const now = new Date().toISOString();
            lastReadTimesRef.current[selectedNote._id] = now;
            localStorage.setItem('noteListLastReadTimes', JSON.stringify(lastReadTimesRef.current));
            
            // Clear unread count for this note
            setUnreadCounts(prev => {
                const updated = { ...prev };
                delete updated[selectedNote._id];
                return updated;
            });
            await sendViaRestAPI(studentId, text);
            // Try sending via socket first (real-time)
            if (SocketService.isConnected()) {
                SocketService.sendMessage(selectedNote._id, text, {
                    onSuccess: () => {
                        console.log('Message sent via socket');
                        // Message will be added via socket receive-message event
                        // No need to refresh lists or reload messages - socket handles it
                    },
                    onError: (error) => {
                        console.error('Socket send error, falling back to REST:', error);
                        // Fallback to REST API
                        sendViaRestAPI(studentId, text);
                    },
                });
            } else {
                // Fallback to REST API if socket not connected
                await sendViaRestAPI(studentId, text);
            }
        } catch (err) {
            console.error('Error sending message:', err);
        }
    };

    const sendViaRestAPI = async (studentId, text) => {
        try {
            await NoteService.sendChatMessage({ 
                studentId, 
                note: text,
                parentNoteId: selectedNote._id
            });
            
            // Only reload messages when using REST API (socket handles it automatically)
            await loadChatMessages(selectedNote._id);
            
            // Don't refresh lists or check unread - WebSocket will handle updates
            // Only refresh if socket is not connected
            if (!SocketService.isConnected()) {
                await loadNotesForStudent(selectedStudent?._id);
                // Update student unread count only if socket is down
                const selectedNoteStudentId = selectedNote?.studentId?._id || selectedNote?.studentId;
                if (selectedNoteStudentId) {
                    const studentIdStr = String(selectedNoteStudentId);
                    const studentNotes = notesList.filter(note => {
                        const noteStId = note?.studentId?._id || note?.studentId;
                        return String(noteStId) === studentIdStr;
                    });
                    if (studentNotes.length > 0) {
                        await checkForUnreadMessages(studentNotes);
                    }
                }
            }
            
            // Auto-scroll to bottom
            setTimeout(() => {
                if (chatMessagesEndRef.current) {
                    chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        } catch (err) {
            console.error('Error sending via REST API:', err);
        }
    };

    const getStudentName = (student) => {
        if (!student) return '-';
        const first = student.firstName || '';
        const last = student.lastName || '';
        return `${first} ${last}`.trim() || '-';
    };

    const getStudentNameFromNote = (note) => {
        const s = note?.studentId;
        if (!s) return '-';
        const first = s.firstName || '';
        const last = s.lastName || '';
        return `${first} ${last}`.trim() || '-';
    };

    const getClassName = (student) => {
        return student?.classId?.className || '-';
    };

    const getClassNameFromNote = (note) => {
        return note?.studentId?.classId?.className || note?.classId?.className || '-';
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTimeShort = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const isMine = (msg) => {
        const me = userDetails?._id;
        const sender = msg?.createdBy?._id;
        return Boolean(me && sender && String(me) === String(sender));
    };

    return (
        <>
            <PageTitle title="Notes List" />
            <div className={classes.yellowHeader}>
                <Typography variant="h5" style={{ fontWeight: 'bold', color: 'white' }}>
                    Messages from Parents
                </Typography>
            </div>

            <Widget>
                <Box className={classes.layout}>
                    {/* Left Panel - Students List */}
                    <Paper className={classes.studentsPanel}>
                        <Box p={2}>
                            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                                Students
                            </Typography>
                        </Box>
                        <Divider />
                        <List dense>
                            {studentsList.length === 0 ? (
                                <Box p={2}>
                                    <Typography variant="body2" style={{ color: '#777' }}>
                                        No students found.
                                    </Typography>
                                </Box>
                            ) : (
                                studentsList.map((student) => {
                                    const selected = selectedStudent?._id && student._id 
                                        ? String(selectedStudent._id) === String(student._id) 
                                        : false;
                                    const studentIdStr = student._id ? String(student._id) : String(student);
                                    const unreadCount = studentUnreadCounts[studentIdStr] || 0;
                                    return (
                                        <React.Fragment key={student._id}>
                                            <ListItem
                                                button
                                                selected={selected}
                                                onClick={() => handleSelectStudent(student)}
                                            >
                                                <Badge
                                                    badgeContent={unreadCount > 0 ? unreadCount : 0}
                                                    color="error"
                                                    overlap="rectangular"
                                                    style={{ width: '100%', marginRight: 8 }}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="body2" style={{ fontWeight: selected ? 700 : 400 }}>
                                                                {getStudentName(student)}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography variant="caption" style={{ fontSize: '0.75rem', color: '#666' }}>
                                                                {getClassName(student)}
                                                            </Typography>
                                                        }
                                                    />
                                                </Badge>
                                            </ListItem>
                                            <Divider />
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </List>
                    </Paper>

                    {/* Right Panel - Notes List */}
                    <Paper className={classes.notesPanel}>
                        <Box p={2} style={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
                            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                                {selectedStudent ? `Notes from ${getStudentName(selectedStudent)}` : 'Select a student'}
                            </Typography>
                        </Box>
                        <Box className={classes.notesContainer}>
                            {!selectedStudent ? (
                                <Box style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                    <Typography variant="body2">
                                        Please select a student from the left panel.
                                    </Typography>
                                </Box>
                            ) : notesList.length === 0 ? (
                                <Box style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                    <Typography variant="body2">
                                        No messages from this student yet.
                                    </Typography>
                                </Box>
                            ) : (
                                notesList.map((note) => {
                                    const unreadCount = unreadCounts[note._id] || 0;
                                    return (
                                        <Badge
                                            key={note._id}
                                            badgeContent={unreadCount > 0 ? unreadCount : 0}
                                            color="error"
                                            overlap="rectangular"
                                            style={{ width: '100%', marginBottom: 16 }}
                                        >
                                            <Box 
                                                className={classes.noteBubble}
                                                onClick={() => handleOpenChat(note)}
                                                style={{ width: '100%' }}
                                            >
                                                <Box className={classes.noteHeader}>
                                                    {note.noteType && (
                                                        <Chip
                                                            label="Submitted"
                                                            size="small"
                                                            style={{
                                                                backgroundColor: '#4caf50',
                                                                color: 'white',
                                                                fontSize: '0.65rem',
                                                                height: '18px',
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                        {note.noteType && (
                                            <Typography className={classes.noteType}>
                                                📋 {note.noteType}
                                            </Typography>
                                        )}
                                        {note.note && (
                                            <Typography className={classes.noteText}>
                                                {note.note}
                                            </Typography>
                                        )}
                                                <Typography className={classes.noteTime}>
                                                    {formatTime(note.createdAt)}
                                                </Typography>
                                            </Box>
                                        </Badge>
                                    );
                                })
                            )}
                        </Box>
                    </Paper>
                </Box>
            </Widget>

            {/* Chat Dialog */}
            <Dialog
                open={chatOpen}
                onClose={handleCloseChat}
                className={classes.chatDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle className={classes.chatHeader}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', color: 'white' }}>
                        {selectedNote ? `${getStudentNameFromNote(selectedNote)} • ${getClassNameFromNote(selectedNote)}` : 'Chat'}
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseChat}
                        style={{ color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.chatMessages}>
                    {chatMessages.length === 0 ? (
                        <Box style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                            <Typography variant="body2">
                                No messages yet. Start a conversation!
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {chatMessages.map((m, idx) => {
                                const isFormNote = m.noteType && m.messageKind !== 'CHAT';
                                return (
                                    <Box
                                        key={m._id || idx}
                                        className={`${classes.chatMessageBubble} ${
                                            isMine(m) ? classes.messageSent : classes.messageReceived
                                        }`}
                                        style={isFormNote ? { backgroundColor: '#fff3cd', border: '1px solid #ffc107', color: '#856404' } : {}}
                                    >
                                        {isFormNote && (
                                            <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                <Typography variant="caption" style={{ fontWeight: 700, fontSize: '0.7rem', opacity: 0.9 }}>
                                                    📋 {m.noteType}
                                                </Typography>
                                                <Chip
                                                    label="Submitted"
                                                    size="small"
                                                    style={{
                                                        backgroundColor: '#4caf50',
                                                        color: 'white',
                                                        fontSize: '0.65rem',
                                                        height: '18px',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        <Typography variant="body2">{m.note || '-'}</Typography>
                                        <Typography variant="caption" style={{ display: 'block', marginTop: 4, opacity: 0.75, fontSize: '0.7rem' }}>
                                            {formatTimeShort(m.createdAt)}
                                        </Typography>
                                    </Box>
                                );
                            })}
                            <div ref={chatMessagesEndRef} />
                        </>
                    )}
                </DialogContent>
                <DialogActions className={classes.chatInput}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Type a message..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && chatInput.trim()) {
                                handleSendMessage();
                            }
                        }}
                        size="small"
                    />
                    <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!chatInput.trim()}
                    >
                        <SendIcon />
                    </IconButton>
                </DialogActions>
            </Dialog>
        </>
    );
}
